import { createComposer, type ComposeOptions } from '@signa/renderer';
import {
  BufferTarget,
  CanvasSource,
  canEncodeVideo,
  Mp4OutputFormat,
  Output,
  Quality,
} from 'mediabunny';
import {
  bitrateFor,
  exportDuration,
  isTruncated,
  progressOf,
  EXPORT_FPS,
  type Size,
} from './encode-plan.js';

/**
 * 合成結果を MP4 に書き出す(#181 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md))。
 *
 * ## 方式
 *
 * **WebCodecs で符号化し、[mediabunny](https://mediabunny.dev/) の出力機能で MP4 に固める**
 * (決定 1・2)。**採る理由は「出力形式がどこでも MP4 / H.264 で揃う」こと** ——
 * `MediaRecorder` は Chrome で WebM、Safari で MP4 に分かれる。**人に渡すファイル**なので、
 * どこで作っても同じ形式である必要がある。
 *
 * ## 素材はシークせず、再生しながら撮る
 *
 * **1/30 秒ずつシークする方式は捨てた**(#181 の実測)。
 * **シークのたびにブラウザがデコードをやり直し、1 フレームに 665ms かかっていた** ——
 * 30 秒の素材で 4 分待つことになる。内訳は次のとおりで、**合成は問題ではなかった:**
 *
 * | 処理 | 実測 |
 * |---|---|
 * | 合成(背景 + WebGL + 転写 + マスク) | **1.65ms** |
 * | シーク + デコード + 符号化 | **約 663ms** |
 *
 * **再生しながら撮れば実時間で進む。** 合成が 1.65ms なので、30fps の予算(33ms)に十分収まる。
 * **実時間より速くはできない**が、シークより 20 倍速い。
 *
 * ## ネットワークを使わない
 *
 * **このモジュールは `fetch` / `XHR` / `WebSocket` を持たない。** 設計書 9.5 の
 * 「素材を永続化しない」は**送信のコードを持たないこと**で担保しており、
 * **生成物も同じ**である —— 書き出した MP4 もサーバへは出さない。
 */

/** 書き出しの失敗の理由。**黙って壊れない**ため、呼び出し側が文言を選べるようにする。 */
export type ExportFailure =
  /** この環境では符号化できない(WebCodecs 非対応・コーデック非対応)。 */
  | 'unsupported'
  /** 利用者が中止した。 */
  | 'aborted'
  /** 合成や符号化の途中で落ちた。 */
  | 'failed';

export type ExportResult =
  | {
      readonly ok: true;
      readonly blob: Blob;
      /** **上限で切ったか**(ADR-0036 決定 6)。切ったときは利用者に伝える。 */
      readonly truncated: boolean;
    }
  | { readonly ok: false; readonly reason: ExportFailure };

export interface ExportVideoOptions extends ComposeOptions {
  /**
   * 書き出しの対象になる動画素材。
   *
   * **長さの決定とシークに使う。** 呼び出し側が
   * [`playableOf`](./playback.ts) で取り出したものをそのまま渡す
   * (**静止画を除き、同じ要素は 1 度だけ**)。
   */
  readonly videos: readonly HTMLVideoElement[];
  /** 進捗(0〜1)。 */
  readonly onProgress?: (ratio: number) => void;
  /** 中止の合図。 */
  readonly signal?: AbortSignal;
}

/**
 * この環境で書き出せるか。**実行時に確かめる**(ADR-0036 決定 8)。
 *
 * **静的なブラウザ判定はしない。** H.264 の符号化は**端末のハードウェア次第**で、
 * ブラウザのバージョンだけでは決まらない。
 *
 * ## 自前でコーデック文字列を組まない
 *
 * **`avc1.42001f`(Baseline Level 3.1)を直に渡して踏み抜いた** ——
 * **Level 3.1 の上限は 1280×720** なので、**1080p の盤面で必ず「非対応」になる。**
 * プロファイルと Level は**寸法とフレームレートから決まる**ので、手で書くと寸法ごとに間違える。
 *
 * **`canEncodeVideo` は、実際に符号化するときと同じ組み立てで判定する** ——
 * 書き出す側([`CanvasSource`](https://mediabunny.dev/))と判定がずれない。
 */
export async function canExportVideo(size: Size): Promise<boolean> {
  try {
    return await canEncodeVideo('avc', {
      width: evenOf(size.width),
      height: evenOf(size.height),
      quality: new Quality({ bitrate: bitrateFor(size) }),
    });
  } catch {
    // 設定が不正だと投げる実装がある。**投げたら「使えない」と見なす。**
    return false;
  }
}

/**
 * いまの状態を MP4 に書き出す。
 *
 * **長さは最も長い素材 1 周、上限 60 秒**(ADR-0036 決定 6)。
 * 素材はループ再生なので 1 周すれば全部の見え方が出る。
 */
export async function exportVideo(options: ExportVideoOptions): Promise<ExportResult> {
  const composer = createComposer(options);
  if (composer === null) return { ok: false, reason: 'unsupported' };

  const size = { width: composer.canvas.width, height: composer.canvas.height };
  if (!(await canExportVideo(size))) {
    composer.dispose();
    return { ok: false, reason: 'unsupported' };
  }

  const durations = options.videos.map((v) => v.duration);
  const duration = exportDuration(durations);
  const truncated = isTruncated(durations);

  const output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() });
  const source = new CanvasSource(composer.canvas, {
    codec: 'avc',
    quality: new Quality({ bitrate: bitrateFor(size) }),
  });
  output.addVideoTrack(source, { frameRate: EXPORT_FPS });

  /*
   * **止めたら中間データを残さない**(受け入れ条件)。
   *
   * `cancel()` はエンコーダと出力先を捨てる。呼ばないと、**中止したつもりで
   * バッファが残り続ける。**
   */
  /** 書き出しの前の再生状態。**終わったら戻す** —— 画面の再生を壊さない。 */
  const before = options.videos.map(snapshotOf);

  const abandon = async (): Promise<void> => {
    restoreAll(options.videos, before);
    composer.dispose();
    try {
      await output.cancel();
    } catch {
      // 既に終わっている・始まっていない場合は投げる。**捨てるだけなので無視してよい。**
    }
  };

  try {
    /*
     * **描き始める前に、素材にフレームを持たせる**(#181 で踏んだ)。
     *
     * **一度も再生・シークしていない `<video>` は、`readyState` が 4(HAVE_ENOUGH_DATA)でも
     * テクスチャへ転送できないことがある** —— `texImage2D` が失敗し、**盤面が黒いまま**になる。
     *
     * 書き出しの 1 枚目は時刻 0 なので、`currentTime` が既に 0 だと
     * **シークがスキップされ、フレームの無い素材を描いてしまう。**
     * ここで一度動かしておけば、以降は普通にシークできる。
     */
    await primeVideos(options.videos);

    await output.start();

    const interval = 1 / EXPORT_FPS;

    if (duration <= 0) {
      /*
       * **動く素材が無い**(静止画だけ / 長さが取れない)。
       * **1 枚だけ出す** —— 0 枚だと中身の無い MP4 になり、利用者には壊れて見える。
       */
      composer.draw();
      await source.add(0, interval);
      options.onProgress?.(1);
    } else {
      /*
       * **素材を先頭から流し、実時間で撮る。**
       *
       * **全部を同時に開始する**ので、画面で見ていた「同期」した状態と同じ並びになる
       * (#123 と同じ論点)。**ループさせる**のは、素材が書き出しより短いとき
       * 表示と同じく繰り返すため(設計書 423)。
       */
      await startCapture(options.videos);

      /*
       * **間に合わないときは素材を遅く再生する**(#181 / 利用者の要件)。
       *
       * **等倍で再生しながら撮ると、処理が重い環境ではフレームが落ちる** ——
       * 実機で **30fps のはずが 14.4fps** になった(30 秒の出力に 432 枚)。
       * 落ちた絵は戻らないので、**書き出しがカクつく。**
       *
       * **素材を遅く流せば、1 枚あたりに使える時間が増える。**
       * 出力のタイムスタンプは**素材上の時刻**なので、
       * **できあがる動画の長さも滑らかさも変わらない** —— 待ち時間だけが延びる。
       *
       * **下限は実時間の 2.5 倍**(`MIN_RATE`)。利用者の要件が
       * 「動画時間の 2〜2.5 倍まで」なので、そこで止める ——
       * それ以上遅くすると、**なめらかさのために待たせすぎる。**
       */
      let rate = 1;
      /** 次に撮りたい時刻(秒)。**30fps の等間隔**を目標にする。 */
      let nextStamp = 0;
      /** 直前に撮った時刻(秒)。**タイムスタンプを単調増加に保つ**ために持つ。 */
      let lastStamp = -1;

      /*
       * **基準にする素材**(#235)。**最も長いもの。**
       *
       * 書き出しの長さ([`exportDuration`](./encode-plan.ts))はこの素材で決まっているので、
       * **撮り終えるまでループしない**(上限 60 秒で切る場合も、切った時点で終わる)——
       * だから**ループの回数を数える必要がない。**
       */
      const reference = longestOf(options.videos);

      for (;;) {
        if (options.signal?.aborted === true) {
          await abandon();
          return { ok: false, reason: 'aborted' };
        }

        /*
         * **素材の実際の位置を見る**(#235 で踏んだ)。
         *
         * **以前は「実経過 × 再生速度」で計算していた** —— 処理が遅れても
         * 素材とずれないようにするためだったが、**逆の事故を見落としていた。**
         *
         * **4K のデコードが追いつかないと、ブラウザはフレームを飛ばして時刻だけを進める。**
         * `playbackRate` を下げても実際の再生は先へ行くので、
         * **計算値が 30 秒を数える間に素材は 60 秒以上進み、倍速・2 ループ分が書き出された。**
         *
         * 素材の位置を基準にすれば、**飛んでも出力の長さは正しくなる**
         * (そのぶんフレームレートは落ちる —— 長さが狂うよりはよい)。
         */
        const mediaTime = reference === null ? duration : reference.currentTime;
        if (mediaTime >= duration) break;

        // **撮る時刻まで素材が進むのを待つ。**
        if (mediaTime < nextStamp) {
          await nextTick();
          continue;
        }

        const begin = performance.now();
        composer.draw();
        /*
         * **時刻は素材の位置。** ただし**単調増加を保つ** ——
         * `add` は時刻が戻ると受け付けない。
         *
         * **`add` は待つ。** 返る Promise が**バックプレッシャー**になっていて、
         * 待たずに回すとエンコーダのキューに積み上がる。
         */
        const timestamp = lastStamp < 0 ? 0 : Math.max(mediaTime, lastStamp + interval / 2);
        await source.add(timestamp, interval);
        lastStamp = timestamp;
        const cost = (performance.now() - begin) / 1000;

        /*
         * **素材が飛んだ分は追いかける。**
         *
         * 等間隔で進めるだけだと、**飛んだ区間を埋めようとして同じ絵を撮り続ける** ——
         * 無駄なフレームで時間を使い、いつまでも追いつかない。
         */
        nextStamp = Math.max(nextStamp + interval, mediaTime);

        /*
         * **1 枚の処理が予算に食い込んでいたら減速する。**
         *
         * 予算は 1/30 秒。**8 割を超えたら**次から遅くする ——
         * ぎりぎりまで待つと、ばらつきでフレームを落とす。
         */
        if (cost > interval * SLOW_THRESHOLD && rate > MIN_RATE) {
          rate = Math.max(MIN_RATE, rate * RATE_STEP);
          applyRate(options.videos, rate);
        }

        options.onProgress?.(progressOf(mediaTime, duration));
        await nextTick();
      }

      restoreAll(options.videos, before);
    }

    await output.finalize();
    const buffer = output.target.buffer;
    if (buffer === null) {
      composer.dispose();
      return { ok: false, reason: 'failed' };
    }

    restoreAll(options.videos, before);
    composer.dispose();
    return { ok: true, blob: new Blob([buffer], { type: 'video/mp4' }), truncated };
  } catch {
    await abandon();
    return { ok: false, reason: 'failed' };
  }
}

/**
 * 素材を時刻 `time` に合わせる。
 *
 * **素材より書き出しが長ければ巻き戻して繰り返す** —— 表示はループ再生
 * (設計書 423)なので、書き出しでも同じ見え方にする。
 *
 * **`seeked` を待たずに描くと前のフレームが写る。** ただし
 * **待ち続けてもいけない** —— 壊れた素材では `seeked` が来ないことがあり、
 * **書き出しがそこで止まる。** 上限を置いて、来なければそのまま進む
 * (**1 フレームずれるより、終わらないほうが痛い**)。
 */
function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    // メタデータが無い(`readyState < 1`)ものはシークできない。そのまま描く。
    if (video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) {
      resolve();
      return;
    }

    const target = time % video.duration;
    // 既にその位置なら待たない(**同じ位置へのシークは `seeked` が来ない**)。
    if (Math.abs(video.currentTime - target) < 1 / (EXPORT_FPS * 4)) {
      resolve();
      return;
    }

    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      video.removeEventListener('seeked', finish);
      resolve();
    };

    const timer = setTimeout(finish, SEEK_TIMEOUT_MS);
    video.addEventListener('seeked', finish, { once: true });
    video.currentTime = target;
  });
}

/** シークの待ちの上限(ms)。**来なければ諦めて進む。** */
const SEEK_TIMEOUT_MS = 2000;

/**
 * 素材の再生速度の下限(#181)。
 *
 * **実時間の 2.5 倍**まで遅くしてよい —— 利用者の要件が
 * 「書き出しは動画時間の 2〜2.5 倍まで」なので、そこで止める。
 */
const MIN_RATE = 0.4;

/** 1 枚の処理がこの割合を超えたら減速する(予算 = 1/30 秒)。 */
const SLOW_THRESHOLD = 0.8;

/** 減速の刻み。**一度に大きく落とさない** —— 速い環境で無駄に待たせない。 */
const RATE_STEP = 0.9;

/**
 * 最も長い素材を返す(#235)。**書き出しの基準になる。**
 *
 * 長さの取れないもの(`NaN` / `Infinity`)は数えない。
 * **1 本も無ければ `null`** —— 呼び出し側は「もう撮り終えた」として扱う。
 */
function longestOf(videos: readonly HTMLVideoElement[]): HTMLVideoElement | null {
  let best: HTMLVideoElement | null = null;
  for (const video of videos) {
    if (!Number.isFinite(video.duration) || video.duration <= 0) continue;
    if (best === null || video.duration > best.duration) best = video;
  }
  return best;
}

/** 素材の再生速度をそろえる。 */
function applyRate(videos: readonly HTMLVideoElement[], rate: number): void {
  for (const video of videos) {
    try {
      video.playbackRate = rate;
    } catch {
      // 受け付けない実装がある。**等倍のまま続ける**(落ちるよりよい)。
    }
  }
}

/**
 * 素材にフレームを持たせる(#181)。
 *
 * **`<video>` を一度も動かさないと、テクスチャへ転送できないことがある。**
 * `readyState` が 4(HAVE_ENOUGH_DATA)でも起きる ——
 * **書き出しも静止画保存も丸ごと黒くなる原因**だった。
 *
 * **いまと違う位置へ動かしてから、元の位置へ戻す。** 同じ位置への代入では
 * `seeked` が来ないので「動かした」ことにならない。
 * **元へ戻すのは、静止画保存が「いま見えている位置」を保存するため** ——
 * 勝手に頭出しすると、画面と保存した絵が食い違う。
 *
 * **表示(ビューアの通常再生)では起きない。** あちらは毎フレーム描き直すので、
 * 1 回目に失敗しても次で戻る。**1 回しか描かない経路だけが踏む。**
 */
export async function primeVideos(videos: readonly HTMLVideoElement[]): Promise<void> {
  await Promise.all(videos.map((video) => primeVideo(video)));
}

async function primeVideo(video: HTMLVideoElement): Promise<void> {
  if (video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) return;

  const before = video.currentTime;
  const away = before < video.duration / 2 ? video.duration / 2 : 0;
  await seekTo(video, away);
  await seekTo(video, before);
}

/**
 * 偶数に丸める。
 *
 * **H.264 の彩度成分は 2×2 画素をまとめて持つ**(4:2:0)ため、
 * **奇数の寸法を受け付けない実装がある。** 判定の時点でそろえておく。
 */
function evenOf(value: number): number {
  return Math.max(2, Math.floor(value / 2) * 2);
}

// ---------------------------------------------------------------------------
// 再生しながら撮る(#181)
// ---------------------------------------------------------------------------

/** 書き出しの前の再生状態。**終わったら戻す。** */
interface PlaybackSnapshot {
  readonly currentTime: number;
  readonly paused: boolean;
  readonly loop: boolean;
  /**
   * 再生速度(#235)。
   *
   * **戻し忘れると次の書き出しに持ち越される** —— 書き出しは処理が重いと
   * `playbackRate` を下げる。戻さないと**2 回目が遅い素材で始まり**、
   * 出力の長さが狂った(倍速・複数ループ)。
   */
  readonly playbackRate: number;
}

function snapshotOf(video: HTMLVideoElement): PlaybackSnapshot {
  return {
    currentTime: video.currentTime,
    paused: video.paused,
    loop: video.loop,
    playbackRate: video.playbackRate,
  };
}

/**
 * 書き出しのために素材を先頭から流す。
 *
 * **ループさせる** —— 素材が書き出しより短いとき、表示と同じく繰り返す(設計書 423)。
 * **ミュートにする** —— 音は使わないうえ、**音があると自動再生が拒否される。**
 *
 * ## そろえてから流す(#245)
 *
 * **シークの所要時間は素材ごとに違う。** [`seekTo`](#seekTo) は `seeked` を待ち(上限 2 秒)、
 * 待ち時間は**解像度・コーデック・デコーダの状態**で変わる。
 *
 * **素材ごとに「シーク → 再生」を直列で回すと、終わった素材から順に流れ始める** ——
 * 4K と FHD が混ざると開きやすく、**250ms ずれれば 30fps で約 7 フレーム**が
 * そのまま出力に入る。書き出しのループは最長素材の位置を基準に撮る(#235)ので
 * **長さは正しいが、素材どうしの相対位置がずれる。**
 *
 * **だから 2 段に分ける。** 全部のシークが終わってから、まとめて再生を始める。
 * **開始は最も遅いシークに合わせて最大 2 秒待つ**が、書き出し自体が数十秒かかるので
 * 誤差の範囲(#245 の同意書 Q1)—— **タイムアウトを縮めると、間に合わなかった素材が
 * ずれたまま残って本末転倒。**
 *
 * **テストのために `export` している。** 検査できるのは**手順の順序**まで ——
 * 符号化そのものは jsdom で動かない([ADR-0019](../../../../docs/adr/0019-dev-foundation.md))。
 */
export async function startCapture(videos: readonly HTMLVideoElement[]): Promise<void> {
  /*
   * **1 段目: そろえる。** 止めて、初期設定して、シークを全部終える。
   */
  await Promise.all(
    videos.map((video) => {
      video.loop = true;
      video.muted = true;
      /*
       * **必ず等倍から始める**(#235)。
       *
       * 前の書き出しで落とした速度が残っていると、**減速の判断が積み上がって**
       * 想定より遅い素材で撮ることになる。
       */
      applyRate([video], 1);
      /*
       * **シークの前に止める**(#245)。**ここを外すとそろわない。**
       *
       * `currentTime` への代入は**再生を止めない。** 流れたままシークすると、
       * **速く終わった素材は 0 から進み始め、遅い素材のシークを待つ間に先行する** ——
       * 2 段目で `play()` を並べても、**既に流れている素材には効かない**(no-op)ので
       * ずれが残る。ビューアは既定で自動再生し、**書き出し中も素材の再生は続く**
       * (`shell.ts` の `beginExportUi` が止めるのは表示の描画だけ)。
       */
      video.pause();
      return seekTo(video, 0);
    }),
  );

  /*
   * **2 段目: まとめて流す。**
   *
   * `map` で `play()` の**呼び出しを連続させてから**待つ。
   * `for (const video of videos) await video.play()` と書くと**再び順番待ち**になり、
   * 直す前と変わらない —— **ここが #245 の要点。**
   */
  await Promise.all(
    videos.map((video) =>
      video.play().catch(() => {
        /*
         * **再生できなくても続ける。** 自動再生を拒まれても、
         * **描けるフレームはある**(prime 済み)—— 止まった絵で書き出すほうが、
         * 何も出さないよりよい。
         */
      }),
    ),
  );
}

/** 再生状態を書き出しの前に戻す。**画面の再生を壊さない。** */
function restoreAll(
  videos: readonly HTMLVideoElement[],
  snapshots: readonly PlaybackSnapshot[],
): void {
  videos.forEach((video, index) => {
    const snapshot = snapshots[index];
    if (snapshot === undefined) return;
    video.loop = snapshot.loop;
    // **速度を必ず戻す**(#235)。残すと次の書き出しに持ち越される。
    applyRate([video], snapshot.playbackRate);
    if (snapshot.paused) video.pause();
    video.currentTime = snapshot.currentTime;
  });
}

/**
 * 次の描画の機会まで待つ。
 *
 * **`requestAnimationFrame` を使う** —— 画面の更新に合わせて進めることで、
 * **素材のデコードが自然に追いつく。** タイマーで詰めるとデコードを追い越し、
 * 同じ絵を何度も撮ることになる。
 */
function nextTick(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}
